import React, { useMemo, useState } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import CheckIcon from 'icons/check.svg';
import MinusIcon from 'icons/minus.svg';
import PersonDetailsIcon from 'icons/person-details.svg';
import { Trans, useTranslation } from 'react-i18next';

import { useLatestVersionQuery } from 'components/hooks/latestVersionQueries';
import { useUpdateProjectPersonnelMutation } from 'components/hooks/projectPersonnelMutations';
import { useProjectPersonnelsQuery } from 'components/hooks/projectPersonnelQueries';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import Checkbox from 'components/application/Checkbox';
import CollectionSelect from 'components/application/CollectionSelect';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';
import DotTextSkeleton from 'components/application/tableSkeletons/DotTextSkeleton';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import AcceptedReviewModal from 'components/projectPersonnel/components/AcceptedReviewModal';
import PersonnelModal from 'components/projectPersonnel/components/PersonnelModal';
import ProjectPersonnelBlankNotice from 'components/projectPersonnel/components/ProjectPersonnelBlankNotice';
import ProjectPersonnelRowOptions from 'components/projectPersonnel/components/ProjectPersonnelRowOptions';
import ProjectPersonnelTrainingStatusDetails from 'components/projectPersonnel/components/ProjectPersonnelTrainingStatusDetails';
import RejectedReviewModal from 'components/projectPersonnel/components/RejectedReviewModal';
import ReviewStatus from 'components/projectPersonnel/components/ReviewStatus';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

import { personDisplayName } from 'components/helpers/users';

export default function ProjectPersonnelTable({
  currentPage,
  onAssignPersonnelClick,
  onProjectPersonnelDestroySuccess,
  onProjectPersonnelToggle,
  onSelectAllProjectPersonnelToggle,
  selectedProjectPersonnelIds,
}) {
  const { addInedibleToast } = useBreadBoard();
  const { isAllowedFeature } = useCurrentActor();

  const { projectId, projectPersonnelPolicy, reviewPolicy } =
    useProjectPersonnelContext();

  const [currentPersonnelId, setCurrentPersonnelId] = useState(null);
  const [currentAcceptedReviewId, setCurrentAcceptedReviewId] = useState(null);
  const [currentRejectedReviewId, setCurrentRejectedReviewId] = useState(null);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectPersonnel.components.ProjectPersonnelTable',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { data: domainLatestVersion, isSuccess: isDomainLatestVersionSuccess } =
    useLatestVersionQuery({ projectId });

  const {
    data: domainProjectPersonnel,
    isFetching: isDomainProjectPersonnelFetching,
    isSuccess: isDomainProjectPersonnelSuccess,
  } = useProjectPersonnelsQuery({ params: { page: currentPage }, projectId });

  const updateProjectPersonnelMutation = useUpdateProjectPersonnelMutation({
    projectId,
  });

  const handleCompanyRoleChange = ({
    companyRoleId,
    displayName,
    projectPersonnelId,
  }) => {
    updateProjectPersonnelMutation.mutate(
      {
        id: projectPersonnelId,
        page: currentPage,
        projectPersonnelParams: { company_role_id: companyRoleId },
      },
      {
        onError: () => {
          addInedibleToast({
            fullMessage: (
              <Trans
                i18nKey='projectPersonnel.components.ProjectPersonnelTable.handleCompanyRoleChange.errorMessage'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ displayName }}
              />
            ),
          });
        },
      },
    );
  };

  const handlePersonnelClick = (personnelId) => {
    setCurrentPersonnelId(personnelId);
  };

  const handleAcceptedSignatureStatusClick = (reviewId) => {
    setCurrentAcceptedReviewId(reviewId);
  };

  const handleRejectedSignatureStatusClick = (reviewId) => {
    setCurrentRejectedReviewId(reviewId);
  };

  const isDigitalSignaturesEnabled = isAllowedFeature('digital_signatures');
  const isTrainingRegisterEnabled = isAllowedFeature('training_register');

  const buildColumnsArgs = {
    canAction:
      projectPersonnelPolicy.canDestroy ||
      reviewPolicy.canCreate ||
      reviewPolicy.canRemind ||
      reviewPolicy.canReset,
    canSelect: projectPersonnelPolicy.canDestroy,
    isDigitalSignaturesEnabled,
    isTrainingRegisterEnabled,
    translations: {
      name: {
        cell: { icon: i18nTCommon('personnel.personnelDetails') },
        header: i18nTCommon('shared.name'),
      },
      role: {
        cell: { selectPlaceholder: i18nT('role.select.placeholder') },
        header: i18nTCommon('shared.role'),
      },
      selector: {
        cell: {
          deselect: i18nTCommon('shared.deselect'),
          select: i18nTCommon('shared.select'),
        },
        header: {
          deselectAll: i18nTCommon('shared.deselectAll'),
          selectAll: i18nTCommon('shared.selectAll'),
        },
      },
      signatureStatus: {
        header: i18nTCommon('projects.signatureStatus'),
      },
      trainingStatus: {
        header: i18nTCommon('training.trainingStatus'),
      },
    },
  };

  const columns = useMemo(
    () => buildColumns(buildColumnsArgs),
    [JSON.stringify(buildColumnsArgs)],
  );

  const isDataPending =
    !(isDomainLatestVersionSuccess || isDomainProjectPersonnelSuccess) ||
    isDomainProjectPersonnelFetching;

  const data = useMemo(() => {
    const projectPersonnel = domainProjectPersonnel?.data || [];

    return isDataPending ?
        Array(projectPersonnel.length || 25).fill({})
      : projectPersonnel;
  }, [domainProjectPersonnel?.data, isDataPending]);

  const included = useMemo(
    () => domainProjectPersonnel?.included || {},
    [domainProjectPersonnel?.included],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      domainLatestVersion,
      included,
      isDataPending,
      isMutatingProjectPersonnel: updateProjectPersonnelMutation.isLoading,
      onAcceptedSignatureStatusClick: handleAcceptedSignatureStatusClick,
      onCompanyRoleChange: handleCompanyRoleChange,
      onPersonnelClick: handlePersonnelClick,
      onProjectPersonnelDestroySuccess,
      onProjectPersonnelToggle,
      onRejectedSignatureStatusClick: handleRejectedSignatureStatusClick,
      onSelectAllProjectPersonnelToggle,
      projectPersonnelPolicy,
      selectedProjectPersonnelIds,
    },
  });

  return isDataPending || data.length > 0 ?
      <>
        <table className='tw-w-full tw-table-fixed tw-border-separate tw-select-none tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    className={classNames(
                      selectedProjectPersonnelIds.size > 0 ?
                        'tw-bg-blue-050'
                      : 'tw-bg-grey-050',
                      header.column.columnDef.meta?.classNames,
                    )}
                    key={header.id}
                  >
                    <div
                      className={classNames(
                        'tw-flex tw-h-10 tw-items-center tw-justify-start tw-truncate tw-p-2 tw-pr-2 tw-text-s tw-font-medium tw-tracking-auto tw-text-grey-900',
                        header.column.getIsFirstColumn() ? 'tw-pl-4'
                        : header.column.columnDef.id === 'name' ? 'tw-pl-12'
                        : 'tw-pl-2',
                      )}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr className='tw-group/row' key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    className={classNames(
                      'tw-h-13 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100',
                      selectedProjectPersonnelIds.has(row.original.id) ?
                        'tw-bg-blue-025'
                      : 'tw-bg-white hover:tw-bg-blue-025 group-hover/row:tw-bg-blue-025',
                      cell.column.columnDef.meta?.classNames,
                    )}
                    key={cell.id}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {currentPersonnelId && (
          <PersonnelModal
            onClose={() => setCurrentPersonnelId(null)}
            personnelId={currentPersonnelId}
          />
        )}
        {isDigitalSignaturesEnabled && (
          <>
            {currentAcceptedReviewId && (
              <AcceptedReviewModal
                onClose={() => setCurrentAcceptedReviewId(null)}
                reviewId={currentAcceptedReviewId}
              />
            )}
            {currentRejectedReviewId && (
              <RejectedReviewModal
                onClose={() => setCurrentRejectedReviewId(null)}
                reviewId={currentRejectedReviewId}
              />
            )}
          </>
        )}
      </>
    : <ProjectPersonnelBlankNotice
        onAssignPersonnelClick={onAssignPersonnelClick}
      />;
}

function buildColumns({
  canAction,
  canSelect,
  isDigitalSignaturesEnabled,
  isTrainingRegisterEnabled,
  translations,
}) {
  return [
    canSelect && {
      id: 'selector',
      header: ({ table }) => {
        const {
          isDataPending,
          onSelectAllProjectPersonnelToggle,
          selectedProjectPersonnelIds,
        } = table.options.meta;

        if (isDataPending) return null;

        const projectPersonnelIds = table.options.data.map(
          (projectPersonnel) => projectPersonnel.id,
        );

        const areProjectPersonnelSelected =
          selectedProjectPersonnelIds.size > 0;

        const areAllProjectPersonnelSelected = [...projectPersonnelIds].every(
          (projectPersonnelId) =>
            selectedProjectPersonnelIds.has(projectPersonnelId),
        );

        return (
          <Checkbox
            checkedIcon={areAllProjectPersonnelSelected ? CheckIcon : MinusIcon}
            isChecked={areProjectPersonnelSelected}
            label={
              areProjectPersonnelSelected ?
                translations.selector.header.deselectAll
              : translations.selector.header.selectAll
            }
            name='select-all-project-personnel'
            onChange={() => {
              onSelectAllProjectPersonnelToggle(projectPersonnelIds);
            }}
          />
        );
      },
      cell: ({ row, table }) => {
        const {
          isDataPending,
          onProjectPersonnelToggle,
          selectedProjectPersonnelIds,
        } = table.options.meta;

        if (isDataPending) return null;

        const { id: projectPersonnelId } = row.original;

        const isSelected = selectedProjectPersonnelIds.has(projectPersonnelId);

        return (
          <div className='tw-flex tw-h-full tw-items-center tw-pl-4 tw-pr-2 hover:tw-bg-blue-050'>
            <Checkbox
              isChecked={isSelected}
              label={
                isSelected ?
                  translations.selector.cell.deselect
                : translations.selector.cell.select
              }
              name='select-project-personnel'
              onChange={() => onProjectPersonnelToggle(projectPersonnelId)}
            />
          </div>
        );
      },
      meta: { classNames: 'tw-w-10' },
    },
    {
      id: 'name',
      header: translations.name.header,
      cell: ({ row, table }) => {
        const { included, isDataPending, onPersonnelClick } =
          table.options.meta;

        if (isDataPending) {
          return (
            <div className='tw-pl-10'>
              <TextSkeleton />
            </div>
          );
        }

        const { id: personnelId } = row.original.relationships.personnel.data;

        const {
          attributes: { firstName, lastName },
          relationships: {
            profilePhoto: { data: profilePhotoData },
          },
        } = included.personnel[personnelId];

        const profilePhoto =
          profilePhotoData && included.profilePhoto[profilePhotoData.id];

        return (
          <div className='tw-flex tw-items-center tw-gap-2 tw-px-2'>
            <ProfilePhotoDisplay
              componentSize='sm'
              photoUrl={profilePhoto?.links?.croppedUrl}
              profileInitials={`${firstName[0]}${lastName[0]}`}
            />
            <span className='tw-truncate tw-font-medium tw-text-grey-900'>
              {personDisplayName({ attributes: { firstName, lastName } })}
            </span>
            <OutlinedButton
              color='grey'
              onClick={() => onPersonnelClick(personnelId)}
              size='xs'
            >
              <PersonDetailsIcon className='tw-h-6 tw-w-8' />
              <span className='tw-sr-only'>{translations.name.cell.icon}</span>
            </OutlinedButton>
          </div>
        );
      },
    },
    {
      id: 'role',
      header: translations.role.header,
      cell: ({ row, table }) => {
        const {
          included,
          isDataPending,
          isMutatingProjectPersonnel,
          onCompanyRoleChange,
          projectPersonnelPolicy,
        } = table.options.meta;

        if (isDataPending) {
          return (
            <div className='tw-px-2'>
              <TextSkeleton />
            </div>
          );
        }

        const {
          id: projectPersonnelId,
          relationships: {
            companyRole: { data: companyRoleData },
            personnel: {
              data: { id: personnelId },
            },
          },
        } = row.original;

        const personnel = included.personnel[personnelId];

        const companyRole =
          companyRoleData && included.companyRole[companyRoleData.id];

        const assignableCompanyRoles =
          personnel.relationships.companyRoles.data.map(
            (companyRole) => included.companyRole[companyRole.id],
          );

        if (
          !projectPersonnelPolicy.canUpdate ||
          assignableCompanyRoles.length < 2
        ) {
          return (
            <div className='tw-px-2'>{companyRole?.attributes.position}</div>
          );
        }

        const options = assignableCompanyRoles.map((assignableCompanyRole) => ({
          label: assignableCompanyRole.attributes.position,
          value: assignableCompanyRole.id,
        }));

        return (
          <div className='tw-max-w-[260px]'>
            <CollectionSelect
              isDisabled={isMutatingProjectPersonnel}
              isRequired={true}
              modifiers={['inline-form']}
              name='companyRoleId'
              onChange={(option) => {
                onCompanyRoleChange({
                  companyRoleId: option.value,
                  displayName: personDisplayName({
                    attributes: personnel.attributes,
                  }),
                  projectPersonnelId,
                });
              }}
              options={options}
              placeholder={translations.role.cell.selectPlaceholder}
              value={
                companyRole &&
                options.find((option) => option.value === companyRole.id)
              }
            />
          </div>
        );
      },
      meta: { classNames: 'tw-w-1/4' },
    },
    isTrainingRegisterEnabled && {
      id: 'trainingStatus',
      header: translations.trainingStatus.header,
      cell: ({ row, table }) => (
        <div className='tw-px-2'>
          {table.options.meta.isDataPending ?
            <DotTextSkeleton />
          : <ProjectPersonnelTrainingStatusDetails
              status={row.original.attributes.trainingStatus}
            />
          }
        </div>
      ),
      meta: { classNames: 'tw-w-[18%]' },
    },
    isDigitalSignaturesEnabled && {
      id: 'signatureStatus',
      header: translations.signatureStatus.header,
      cell: ({ row, table }) => {
        const {
          domainLatestVersion,
          included,
          isDataPending,
          onAcceptedSignatureStatusClick,
          onRejectedSignatureStatusClick,
        } = table.options.meta;

        if (isDataPending || !domainLatestVersion) {
          return (
            <div className='tw-px-2'>
              <TextSkeleton />
            </div>
          );
        }

        const {
          latestReview: { data: latestReviewData },
          personnel: {
            data: { id: personnelId },
          },
        } = row.original.relationships;

        const review = latestReviewData && included.review[latestReviewData.id];

        const reviewedVersion =
          review?.relationships.reviewedVersion.data?.id &&
          included.version[review.relationships.reviewedVersion.data.id];

        return (
          <div className='tw-px-2'>
            <ReviewStatus
              domainLatestVersion={domainLatestVersion}
              onAcceptedClick={onAcceptedSignatureStatusClick}
              onRejectedClick={onRejectedSignatureStatusClick}
              personnel={included.personnel[personnelId]}
              review={review}
              reviewedVersion={reviewedVersion}
            />
          </div>
        );
      },
      meta: { classNames: 'tw-w-[18%]' },
    },
    canAction && {
      id: 'actions',
      cell: ({ row, table }) => {
        const { included, isDataPending, onProjectPersonnelDestroySuccess } =
          table.options.meta;

        if (isDataPending) return null;

        const {
          id: projectPersonnelId,
          relationships: {
            latestReview: { data: latestReviewData } = {},
            personnel: {
              data: { id: personnelId },
            },
          },
        } = row.original;

        return (
          <div className='tw-px-2'>
            <ProjectPersonnelRowOptions
              onDestroySuccess={() => {
                onProjectPersonnelDestroySuccess(projectPersonnelId);
              }}
              personnel={included.personnel[personnelId]}
              projectPersonnelId={projectPersonnelId}
              review={latestReviewData && included.review[latestReviewData.id]}
            />
          </div>
        );
      },
      meta: { classNames: 'tw-w-12' },
    },
  ].filter(Boolean);
}
